'use client';

import { useEffect } from 'react';

/**
 * ### useKeyPress
 *
 * @description
 * This hook listens for a specific key press and calls the provided function.
 * Defaults to listening for the Enter key if no key is specified.
 *
 * @example
 * // Listen for Enter key (default)
 * useKeyPress(() => {
 *   console.log('Enter key pressed');
 * });
 *
 * // Listen for a specific key
 * useKeyPress(() => {
 *   console.log('Escape key pressed');
 * }, 'Escape');
 *
 * @param onKeyPress - The function to call when the specified key is pressed
 * @param key - The key to listen for (defaults to 'Enter')
 */
export const useKeyPress = (
  onKeyPress: (event: KeyboardEvent) => void | Promise<void>,
  key = 'Enter',
) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === key) {
        event.preventDefault();
        void onKeyPress(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress, key]);
};

// Keep the original useEnterKey as an alias for backward compatibility
export const useEnterKey = (onEnterPress: (event: KeyboardEvent) => void | Promise<void>) => {
  return useKeyPress(onEnterPress, 'Enter');
};

export default useKeyPress;

export const useEscapeKey = (onEscapePress: (event: KeyboardEvent) => void | Promise<void>) => {
  return useKeyPress(onEscapePress, 'Escape');
};
